<script setup>
import { computed } from 'vue'
import { getCardStyle } from '../../config/cardStyles.js'

const props = defineProps({
  balance: {
    type: String,
    default: '0.0000'
  },
  currency: {
    type: String,
    default: 'BTC'
  },
  bgStyle: {
    type: Number,
    default: 0
  },
  message: {
    type: String,
    default: ''
  }
})

// Get current card style from centralized config
const currentStyle = computed(() => {
  return getCardStyle(props.bgStyle)
})

// Get current background (WebP → JPEG → gradient fallback)
const currentBackground = computed(() => {
  const style = currentStyle.value

  if (style.imageWebP) {
    return `url(${style.imageWebP}), url(${style.image}), ${style.background}`
  }

  if (style.image) {
    return `url(${style.image}), ${style.background}`
  }

  return style.background
})
</script>

<template>
  <div
    class="gift-card-preview"
    :style="{
      background: currentBackground,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    }"
  >
    <!-- Central Content: Message + Balance -->
    <div class="content-center">
      <div class="text-center">
        <!-- Message -->
        <div v-if="message" class="card-message">
          {{ message }}
        </div>

        <!-- Balance -->
        <div class="balance-value">
          {{ balance }}
        </div>
        <div class="currency-ticker">
          {{ currency }}
        </div>
      </div>
    </div>

    <!-- Card decorative shine overlay -->
    <div class="card-shine"></div>
  </div>
</template>

<style scoped>
.gift-card-preview {
  @apply flex flex-col relative shadow-sm flex-shrink-0;
  width: 140px;
  height: 90px;
  border-radius: 10px;
  transition: all 0.3s ease;
  overflow: hidden;
}

.gift-card-preview:hover {
  @apply shadow-md;
  transform: scale(1.03);
}

/* Card decorative shine overlay */
.card-shine {
  @apply absolute pointer-events-none;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    45deg,
    transparent 30%,
    rgba(255, 255, 255, 0.1) 50%,
    transparent 70%
  );
  transform: rotate(45deg);
}

/* Content center */
.content-center {
  @apply absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full flex items-center justify-center;
}

/* Message */
.card-message {
  @apply text-[8px] text-black mb-0.5;
  font-family: 'Kalam', cursive;
  font-weight: 400;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100px;
}

/* Balance */
.balance-value {
  @apply text-[10px] text-black leading-none;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 900;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

/* Currency */
.currency-ticker {
  @apply text-[7px] text-black;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 700;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}
</style>
